import { useEffect, useState } from 'react';
import { emptyExtras, type ComposerExtras } from './contracts';
const key = (owner: string) => `truxsocial:composer-attachments:${owner}`;
export function clearExtrasRecovery(owner: string) {
  try {
    localStorage.removeItem(key(owner));
  } catch {
    /* Browser storage may be unavailable. */
  }
}
function read(owner: string): ComposerExtras | null {
  try {
    const raw = localStorage.getItem(key(owner));
    if (!raw || raw.length > 40000) return null;
    const record = JSON.parse(raw);
    if (Date.now() - record.savedAt > 7 * 86400000) return null;
    const a = record.attachments ?? {},
      next = emptyExtras();
    if (
      a.context &&
      typeof a.context.body === 'string' &&
      Array.isArray(a.context.sources) &&
      a.context.sources.length <= 8 &&
      a.context.sources.every(
        (s: { url?: unknown; label?: unknown }) =>
          typeof s.url === 'string' && typeof s.label === 'string',
      )
    )
      next.attachments.context = a.context;
    if (typeof a.cherishPrompt === 'string') next.attachments.cherishPrompt = a.cherishPrompt;
    if (a.mood && typeof a.mood.label === 'string' && typeof a.mood.emoji === 'string')
      next.attachments.mood = a.mood;
    next.question = record.question === true;
    const c = record.collaboration;
    if (
      c &&
      typeof c.slug === 'string' &&
      typeof c.title === 'string' &&
      typeof c.body === 'string' &&
      Number.isInteger(c.revision) &&
      Number.isInteger(c.version)
    )
      next.collaboration = c;
    const r = record.ring;
    if (
      r &&
      typeof r.id === 'string' &&
      typeof r.name === 'string' &&
      Number.isInteger(r.revision) &&
      Array.isArray(r.people) &&
      r.people.every((p: { handle?: unknown }) => typeof p.handle === 'string')
    )
      next.ring = r;
    const p = record.perspective;
    if (
      p &&
      typeof p.slug === 'string' &&
      typeof p.title === 'string' &&
      Number.isInteger(p.revision) &&
      p.data &&
      Array.isArray(p.data.viewpoints) &&
      p.data.viewpoints.every(
        (v: { id?: unknown; text?: unknown; label?: unknown }) =>
          typeof v.id === 'string' &&
          (v.text == null || typeof v.text === 'string') &&
          (v.label == null || typeof v.label === 'string'),
      )
    )
      next.perspective = p;
    return Object.keys(next.attachments).length ||
      next.question ||
      next.collaboration ||
      next.ring ||
      next.perspective
      ? next
      : null;
  } catch {
    return null;
  }
}
export function useExtrasRecovery(
  owner: string,
  enabled: boolean,
  extras: ComposerExtras,
  onRestore: (v: ComposerExtras) => void,
) {
  const [pending, setPending] = useState(() => (enabled ? read(owner) : null));
  useEffect(() => {
    if (!enabled || pending) return;
    try {
      if (
        Object.keys(extras.attachments).length ||
        extras.question ||
        extras.collaboration ||
        extras.ring ||
        extras.perspective
      )
        localStorage.setItem(
          key(owner),
          JSON.stringify({
            attachments: extras.attachments,
            question: extras.question,
            collaboration: extras.collaboration,
            ring: extras.ring,
            perspective: extras.perspective,
            savedAt: Date.now(),
          }),
        );
      else localStorage.removeItem(key(owner));
    } catch {
      /* Composer stays usable if browser storage is unavailable. */
    }
  }, [owner, enabled, extras, pending]);
  return {
    pending,
    restore() {
      if (pending) onRestore(pending);
      setPending(null);
    },
    discard() {
      try {
        localStorage.removeItem(key(owner));
      } catch {
        /* no storage */
      }
      setPending(null);
    },
  };
}
